"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader, CircleCheck, FlaskConical, User, Settings2 } from "lucide-react";
export default function SynthesisRequestForm() {
  return <section data-section-id="223198" id="apply-to-join-cynthylee" className="py-12 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-lg bg-secondary/40 blur-3xl opacity-50" />
        <div className="absolute bottom-[10%] -left-[10%] w-[40%] h-[40%] rounded-lg bg-primary/5 blur-3xl opacity-50" />
      </div>

      <div className="container mx-auto px-4 relative z-10 max-w-4xl">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-default font-bold text-foreground mb-4">
            Apply to Join Cynthylee
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto font-default text-lg">
            Become part of a compassionate team dedicated to providing premium home care in Brighton & Hove. Share your details below to start your journey with us.
          </p>
        </div>

        <FormProvider formKey="apply_form_careers" formId="apply_form_careers" sectionName="Apply to Join Cynthylee" className="bg-card border border-border shadow-xl shadow-shadow-color/5 rounded-2xl p-6 md:p-10" defaultValues={{
        name: "",
        email: "",
        phone: "",
        position: "Caregiver",
        cv_link: "",
        personal_statement: ""
      }} validationRules={{
        name: {
          required: "Full name is required"
        },
        email: {
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        },
        phone: {
          required: "Phone number is required"
        },
        position: {
          required: "Please select a position"
        },
        cv_link: {
          required: "Please provide a link to your CV"
        },
        personal_statement: {
          required: "Please tell us why you care"
        }
      }} submitText="Submit Application" successMessage="Thank you for your interest in Cynthylee Health Care. Our recruitment team will review your application and contact you shortly.">
          <InnerSynthesisForm />
        </FormProvider>
      </div>
    </section>;
}
function InnerSynthesisForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-16 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 rounded-lg bg-secondary flex items-center justify-center mb-4">
          <CircleCheck className="size-10 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground">Application Sent</h3>
        <p className="text-muted-foreground max-w-md font-default">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-8 rounded-lg border-primary text-primary hover:bg-primary/5">
          Submit Another Application
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      {}
      <div className="space-y-6">
        <div className="flex items-center gap-3 border-b border-border pb-4">
          <div className="bg-primary/10 p-2 rounded-lg">
            <User className="size-5 text-primary" />
          </div>
          <h3 className="text-lg font-default font-semibold text-foreground">01. Personal Information</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="name" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default font-medium">Full Name <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Input placeholder="John Smith" className="bg-background border-input focus:ring-primary rounded-lg" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default font-medium">Email Address <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Input type="email" placeholder="info@cynthyleehealthcare.com" className="bg-background border-input focus:ring-primary rounded-lg" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" render={({
          field
        }) => <FormItem className="md:col-span-2">
                <FormLabel className="font-default font-medium">Phone Number <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="01273 000000" className="bg-background border-input focus:ring-primary rounded-lg" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className="space-y-6">
        <div className="flex items-center gap-3 border-b border-border pb-4">
          <div className="bg-primary/10 p-2 rounded-lg">
            <FlaskConical className="size-5 text-primary" />
          </div>
          <h3 className="text-lg font-default font-semibold text-foreground">02. Role & Experience</h3>
        </div>

        <FormField control={control} name="personal_statement" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-medium">Tell us why you care <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Textarea placeholder="Share your passion for caregiving and why you'd like to join Cynthylee..." className="min-h-[120px] font-default text-base bg-background border-input focus:ring-primary rounded-lg resize-y" {...field} />
              </FormControl>
              <FormDescription className="font-default text-xs text-muted-foreground">
                We value personality and compassion. Tell us what motivates you to help others.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <div className="space-y-6">
        <div className="flex items-center gap-3 border-b border-border pb-4">
          <div className="bg-primary/10 p-2 rounded-lg">
            <Settings2 className="size-5 text-primary" />
          </div>
          <h3 className="text-lg font-default font-semibold text-foreground">03. Application Details</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="cv_link" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default font-medium">CV Link (Google Drive/Dropbox) <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Input type="text" placeholder="https://link-to-your-cv.com" className="bg-background border-input focus:ring-primary rounded-lg" {...field} />
                </FormControl>
                <FormDescription className="font-default text-xs text-muted-foreground">
                  Please provide a public link to your current CV.
                </FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="position" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default font-medium">Desired Position <span className="text-destructive">*</span></FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="bg-background border-input focus:ring-primary rounded-lg">
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="rounded-lg">
                    <SelectItem value="Caregiver">Caregiver</SelectItem>
                    <SelectItem value="Live-in Care">Live-in Care Specialist</SelectItem>
                    <SelectItem value="Registered Nurse">Registered Nurse</SelectItem>
                    <SelectItem value="Administrative">Administrative Support</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
          <div className="text-destructive mt-0.5"></div>
          <p className="text-destructive text-sm font-default">{submitError}</p>
        </div>}

      <div className="pt-6 border-t border-border">
        <Button type="submit" disabled={isSubmitting} size="lg" className="w-full md:w-auto md:min-w-[240px] font-default font-semibold tracking-wide bg-primary text-primary-foreground hover:opacity-90 rounded-lg shadow-md">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Sending Application...
            </> : 'Submit Application'}
        </Button>
      </div>
    </Form>;
}